import { useState } from "react";
import { Zap, AlertCircle, Star } from "lucide-react";
import { useNovexData } from "@/hooks/useNovexData";
import { ExplainButton } from "@/components/ExplainButton";
import { SourceBadge } from "@/components/SourceBadge";
import { useWatchlist } from "@/context/WatchlistContext";
import { useLang } from "@/context/LanguageContext";
import { matchesWatchlist, watchedKeywords } from "@/lib/assets";

export function LiveNewsFeed({ compact = false, urgentOnly = false }) {
  const { t, lang } = useLang();
  const ago = (ts) => {
    const d = new Date(ts);
    if (isNaN(d)) return "";
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}min`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return d.toLocaleDateString(lang === "en" ? "en-US" : "fr-FR", { day: "numeric", month: "short" });
  };
  const { status, data, error, source } = useNovexData(
    `/novex/news?filter=${urgentOnly ? "urgent" : "all"}&limit=${compact ? 20 : 60}`,
    { live: true, liveMs: 30000 }
  );
  const { watchlist } = useWatchlist();
  const [onlyWatch, setOnlyWatch] = useState(false);
  const hasKw = watchedKeywords(watchlist).length > 0;
  const all = data?.items || [];
  const items = onlyWatch && hasKw ? all.filter((it) => matchesWatchlist(it.formatted_text, watchlist)) : all;

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("common.live")}</span>
          <SourceBadge source={source} />
          {hasKw && (
            <button onClick={() => setOnlyWatch((v) => !v)} data-testid="news-watchlist-toggle"
              className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${onlyWatch ? "border-amber-500/60 bg-amber-500/15 text-amber-200" : "border-[#222733] bg-[#11141A] text-slate-400 hover:text-white"}`}>
              <Star className={`h-2.5 w-2.5 ${onlyWatch ? "fill-amber-400 text-amber-400" : ""}`} /> {t("c.watchlist")}
            </button>
          )}
        </div>
        <span className="text-[10px] text-slate-600">{t("c.autoRefresh")}</span>
      </div>

      {status === "loading" ? (
        <div className="space-y-2">{[...Array(compact ? 4 : 8)].map((_, i) => <div key={i} className="h-12 animate-pulse rounded bg-slate-800/50" />)}</div>
      ) : error && items.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-amber-500/80"><AlertCircle className="h-4 w-4" /> {error}</div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500">{onlyWatch ? t("c.newsNoneWatch") : t("c.newsNone")}</p>
      ) : (
        <div className={`overflow-y-auto pr-1 ${compact ? "max-h-[380px]" : "max-h-[calc(100vh-16rem)]"}`} data-testid="news-feed">
          {items.map((it, i) => (
            <div key={`${it.id || "n"}-${i}`} className={`group relative flex gap-3 border-l-2 py-2.5 pl-3 transition-colors hover:bg-[#161A22] ${it.is_urgent ? "border-[#EF4444] bg-[#EF4444]/5" : "border-[#222733]"}`} data-testid="news-item">
              <div className="min-w-[42px] pt-0.5 font-mono text-xs text-slate-500">{ago(it.created_at)}</div>
              <div className="min-w-0 flex-1">
                {it.is_urgent && <span className="mr-1.5 inline-flex items-center gap-0.5 rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-400"><Zap className="h-2.5 w-2.5" />Urgent</span>}
                <span className="text-sm leading-snug text-slate-200">{it.formatted_text}</span>
                {(() => {
                  const parts = [...new Set([it.provider, it.author, it.source].filter(Boolean))];
                  return parts.length ? <div className="mt-0.5 text-[10px] text-slate-600">{parts.join(" · ")}</div> : null;
                })()}
              </div>
              <div className="absolute right-1 top-1.5 opacity-0 transition-opacity group-hover:opacity-100">
                <ExplainButton kind="actualité de marché" text={it.formatted_text} label={t("c.explain")} testid={`explain-news-${it.id}`} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
